import React, { useEffect, useMemo, useRef } from 'react';

const FilingVolumeChart = ({ data, color = 'var(--c-black)' }) => {
  const linePathRef = useRef(null);
  const areaPathRef = useRef(null);

  const width = 300;
  const height = 140;
  const yMin = 130;
  const yMax = 20;
  const heightRange = yMin - yMax;

  const { points, pathD, areaPathD } = useMemo(() => {
    if (!data || data.length === 0) {
      return { points: [], pathD: '', areaPathD: '' };
    }

    const maxValue = Math.max(...data.map((d) => d.value), 1);
    const nextPoints = data.map((d, i) => {
      const x = data.length > 1 ? (i / (data.length - 1)) * width : width / 2;
      const y = yMin - (d.value / maxValue) * heightRange;
      return { x, y, stage: d.stage, value: d.value };
    });

    let nextPathD = `M ${nextPoints[0].x} ${nextPoints[0].y}`;
    for (let i = 1; i < nextPoints.length; i++) {
      const prev = nextPoints[i - 1];
      const curr = nextPoints[i];
      const cpX1 = prev.x + (curr.x - prev.x) / 2;
      const cpY1 = prev.y;
      const cpX2 = prev.x + (curr.x - prev.x) / 2;
      const cpY2 = curr.y;
      nextPathD += ` C ${cpX1} ${cpY1}, ${cpX2} ${cpY2}, ${curr.x} ${curr.y}`;
    }

    return {
      points: nextPoints,
      pathD: nextPathD,
      areaPathD: `${nextPathD} L ${width} ${yMin} L 0 ${yMin} Z`,
    };
  }, [data, heightRange, width, yMin]);

  useEffect(() => {
    const lineEl = linePathRef.current;
    if (!lineEl || !pathD) return;
    lineEl.style.setProperty('--line-length', String(lineEl.getTotalLength()));
  }, [pathD, data]);

  if (!data || data.length === 0) return null;

  return (
    <div style={{ width: '100%' }}>
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} style={{ overflow: 'visible' }}>
        <line x1="0" y1={yMin} x2={width} y2={yMin} stroke="var(--border)" strokeWidth="1" />
        <path ref={areaPathRef} d={areaPathD} fill={color} className="insights-chart-line-area" />
        <path
          ref={linePathRef}
          d={pathD}
          fill="transparent"
          stroke={color}
          strokeWidth="2"
          className="insights-chart-line-path"
        />
        {points.map((p, i) => (
          <g key={i} className="insights-chart-line-point" style={{ '--point-delay': `${0.35 + i * 0.08}s` }}>
            <circle cx={p.x} cy={p.y} r="4" fill="#fff" stroke={color} strokeWidth="2" />
            <text x={p.x} y={p.y - 10} textAnchor="middle" fill="var(--c-black)" fontSize="10" fontWeight="600">
              {p.value}
            </text>
          </g>
        ))}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
        {points.map((p) => (
          <div key={p.stage} style={{ fontSize: '12px', color: 'var(--text3)', textAlign: 'center', flex: 1 }}>
            {p.stage}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FilingVolumeChart;
